// frontend/src/components/homepage/SectionShell.js
//
// The one <section> wrapper every live-mesh homepage section uses: the shared
// vertical rhythm, the shared measure, the navbar-clearing scroll offset, and
// the data attributes the background engines read.
//
// WHY THE data-* ATTRIBUTES LIVE HERE. The mesh engine decides its formation and
// opacity from whichever section is centred in the viewport, and it does that by
// observing `[data-mesh]` elements rather than by holding a list of section
// names. That way reordering, adding or removing a section cannot desync the
// background from the page - the DOM is the only source. Setting them anywhere
// but the section wrapper would put the observer on the wrong box.
//
// `data-live` (perf batch, 2026-09-04) is the third attribute: "true" while
// the section is within ~a quarter of a viewport of the screen, "false"
// otherwise, from the shared useInViewGate. The stylesheets pause every
// infinite CSS loop under `[data-live="false"]` (homepage.css OFF-SCREEN), so
// a dozen decorative loops stop costing frames the visitor cannot see. It
// starts "true" - the prerendered snapshot carries "true" for every section
// (the gate is forced open there), and the hydrating client must render the
// same markup; the observer's first delivery corrects it within a frame.
//
// `data-lf-section` is the fourth: the name the anonymous engagement tracker
// (utils/engagementBeacon.js) attributes time to. Derived from the anchor `id`
// when that id is on the allowlist (utils/engagementSections.js), so every
// anchored section is measured with no per-section wiring; an explicit
// `trackSection` wins, for the sections that have no id (the CTA panels).

import React from "react";
import { Box } from "@mui/material";
import useInViewGate from "../../motion/useInViewGate";
import { resolveSectionTag } from "../../utils/engagementSections";

/** How far outside the viewport a section still counts as live. */
export const LIVE_ROOT_MARGIN = "25% 0px 25% 0px";

/**
 * @param {object} props
 * @param {string} [props.id] anchor id - also what Navbar's scroll-spy observes.
 * @param {string} [props.scene] scene wash key, read by SceneWash (P.3).
 * @param {string} [props.mesh] mesh formation: cloud | pipeline | core | disperse.
 * @param {string|number} [props.meshAlpha] mesh opacity while this section leads.
 * @param {boolean} [props.wrap] render the inner measure box (default true).
 * @param {object} [props.sx] extra styles for the <section> element.
 * @param {object} [props.innerSx] extra styles for the inner measure box.
 * @param {string} [props.trackSection] engagement section name; wins over the
 *   id-derived one. Off-allowlist names are not rendered.
 * @param {React.ReactNode} props.children
 */
export default function SectionShell({
  id,
  scene,
  mesh,
  meshAlpha,
  wrap = true,
  sx,
  innerSx,
  trackSection,
  children,
  ...rest
}) {
  const [gateRef, live] = useInViewGate({ rootMargin: LIVE_ROOT_MARGIN, initialInView: true });
  const inner = wrap ? (
    <Box
      sx={{
        // The prototype's `.wrap`, literal: `min(1440px, calc(100% - 80px))`
        // - a 1440px measure with a 40px gutter EACH side (80px total). The
        // P.10 fidelity pass found this at 40px total, 20px wider per side than
        // the design at every width.
        width: "min(1440px, calc(100% - 80px))",
        mx: "auto",
        ...innerSx,
      }}
    >
      {children}
    </Box>
  ) : (
    children
  );

  return (
    <Box
      component="section"
      ref={gateRef}
      id={id}
      data-scene={scene}
      data-mesh={mesh}
      data-alpha={meshAlpha}
      data-live={live ? "true" : "false"}
      data-lf-section={resolveSectionTag(id, trackSection)}
      sx={{
        position: "relative",
        // Sections sit ABOVE the fixed mesh/grain/vignette layers.
        zIndex: 2,
        // Owner round 3: the vertical rhythm tightens by 20% - 120 -> 96 (md)
        // and 84 -> 64 (xs). The change is safely made HERE, on the default,
        // because every section ABOVE the clients block (which is where the
        // owner asked the tightening to start) sets its own padding and is
        // therefore untouched: HeroLive passes `pt`/`pb` on the SAME
        // {xs, md} breakpoints, which are merged after this key and win at both
        // widths, and TickerLive is not a SectionShell at all. What this
        // default still governs, all of it at or below clients: features,
        // opportunity types, pricing, FAQ, the blog's BOTTOM padding (it
        // overrides `pt` only), and the how-it-works block in its MOBILE
        // stacked form (the pinned desktop form is `py: 0`).
        py: { xs: "64px", md: "96px" },
        // Clear the fixed navbar when an anchor is jumped to - the same offsets
        // the current homepage uses.
        scrollMarginTop: { xs: "72px", md: "88px" },
        ...sx,
      }}
      {...rest}
    >
      {inner}
    </Box>
  );
}
